import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  AURA_CHAKRA_LABELS,
  AURA_CHAKRA_THEMES,
  AURA_CHAKRAS,
  AURA_CHECKIN_KEYS,
  AURA_CHECKIN_PROMPTS,
  AURA_DIMENSION_LABELS,
  AURA_DIMENSIONS,
  type AuraChakra,
  type AuraCheckIn,
  type AuraCheckInKey,
  type AuraDimension,
} from '@mlm/shared/coaching';
import { Block, CoachButton, ErrorNote, Eyebrow, Meter, Screen } from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { coaching, type AuraColorReading, type AuraResponse } from '../lib/api';
import { useFeatureLocked } from '../lib/queries';

const SCALE = [1, 2, 3, 4, 5] as const;
const NEUTRAL: AuraCheckIn = { mood: 3, energy: 3, confidence: 3, stress: 3, focus: 3 };

/**
 * The aura reading.
 *
 * It begins with the five questions, and the screen says plainly that the
 * reading is computed from them. Nothing here takes a photograph: the
 * prototype allowed a selfie and used it only as a backdrop, which invites
 * exactly the misunderstanding this screen exists to avoid.
 */
export function AuraPage() {
  const locked = useFeatureLocked('aura');
  const [checkIn, setCheckIn] = useState<AuraCheckIn>(NEUTRAL);
  const read = useMutation({ mutationFn: () => coaching.aura(checkIn) });

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Aura" title="Your aura today">
        <ProLock feature="aura" />
      </Screen>
    );
  }

  if (read.data) {
    return (
      <Reading
        aura={read.data.aura}
        onAgain={() => {
          setCheckIn(NEUTRAL);
          read.reset();
        }}
      />
    );
  }

  return (
    <Screen
      back="/align"
      eyebrow="Aura"
      title="How is today, honestly?"
      subtitle="Five questions. The reading is worked out from your answers, your cycles and what you have actually practised this week."
    >
      <section className="space-y-7 border-b border-border px-5 py-6">
        {AURA_CHECKIN_KEYS.map((key) => (
          <Question
            key={key}
            field={key}
            value={checkIn[key]}
            onChange={(v) => setCheckIn((c) => ({ ...c, [key]: v }))}
          />
        ))}
      </section>
      {read.isError && <ErrorNote error={read.error} retry={() => read.mutate()} />}
      <section className="border-b border-border px-5 py-6">
        <CoachButton kind="gold" onClick={() => read.mutate()} disabled={read.isPending}>
          {read.isPending ? 'Reading…' : 'Read my aura'}
        </CoachButton>
      </section>
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        There is no photograph and no scan. This is a mirror of what you just told us.
      </p>
    </Screen>
  );
}

function Question({
  field,
  value,
  onChange,
}: {
  field: AuraCheckInKey;
  value: number;
  onChange: (v: number) => void;
}) {
  const prompt = AURA_CHECKIN_PROMPTS[field];
  return (
    <fieldset>
      <legend className="eyebrow">{prompt.label}</legend>
      <div className="mt-3 flex gap-2" role="group">
        {SCALE.map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={value === n}
            aria-label={`${prompt.label} ${n} of 5`}
            onClick={() => onChange(n)}
            className={
              value === n
                ? 'h-11 flex-1 border border-verde bg-verde font-mono text-sm text-white'
                : 'h-11 flex-1 border border-[rgba(16,32,26,0.22)] font-mono text-sm text-ink hover:border-[rgba(16,32,26,0.5)]'
            }
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[0.625rem] uppercase tracking-[0.12em] text-sage-deep">
        <span>{prompt.low}</span>
        <span>{prompt.high}</span>
      </div>
    </fieldset>
  );
}

function Reading({ aura, onAgain }: { aura: AuraResponse; onAgain: () => void }) {
  return (
    <Screen
      back="/align"
      eyebrow={`Aura · ${aura.strength} of 100`}
      title={aura.colors.primary.name}
      subtitle={aura.overview}
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-4">Your three colours</Eyebrow>
        <ul className="space-y-5">
          {[aura.colors.primary, aura.colors.secondary, aura.colors.supporting].map((c) => (
            <ColorRow key={c.key} color={c} />
          ))}
        </ul>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-5">The seven centres</Eyebrow>
        <div className="space-y-5">
          {AURA_CHAKRAS.map((c) => (
            <div key={c}>
              <Meter label={AURA_CHAKRA_LABELS[c as AuraChakra]} value={aura.chakras[c] ?? 0} />
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {AURA_CHAKRA_THEMES[c as AuraChakra]}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-5">Where the day is pointing</Eyebrow>
        <div className="space-y-5">
          {AURA_DIMENSIONS.map((d) => (
            <Meter
              key={d}
              label={AURA_DIMENSION_LABELS[d as AuraDimension]}
              value={aura.dimensions[d] ?? 0}
            />
          ))}
        </div>
      </section>

      <Block eyebrow="One thing today">{aura.suggestion}</Block>

      <section className="border-b border-border px-5 py-6">
        <CoachButton onClick={onAgain}>Answer again</CoachButton>
        <p className="eyebrow mt-4">
          {aura.practiceSessions === 0
            ? 'No practices recorded this week'
            : `${aura.practiceSessions} ${aura.practiceSessions === 1 ? 'practice' : 'practices'} recorded this week`}
        </p>
      </section>

      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">{aura.disclaimer}</p>
    </Screen>
  );
}

function ColorRow({ color }: { color: AuraColorReading }) {
  return (
    <li className="flex items-start gap-3.5">
      <span
        className="mt-0.5 h-9 w-9 shrink-0 rounded-full ring-1 ring-inset ring-[rgba(16,32,26,0.22)]"
        style={{ backgroundColor: color.hex }}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-[0.95rem] text-ink">
          {color.name} <span className="eyebrow ml-1">{color.strength}</span>
        </p>
        <p className="mt-1 text-sm leading-relaxed text-ink/70">{color.theme}</p>
      </div>
    </li>
  );
}
